/**
 * Enzyme activity Explore (CT-INTERACT): the learner types a temperature and a pH and the model
 * shows the substituted Q₁₀, denaturation and pH-bell steps from the same functions that draw the
 * curves in `polymersBiomoleculesFood.ts`. Each curve is the teaching model's shape in relative
 * units with the other variable at its optimum; the two are not combined into one number because
 * the reviewed model does not define how they interact.
 */
import { firstModelError, type ExploreChallenge, type ModelField, type WorkedStep } from './modelInputContract';
import {
  ACTIVITY_Q10,
  DENATURATION_SPAN_C,
  PEAK_ACTIVITY,
  PH_BELL_WIDTH,
  PH_RANGE,
  TEMPERATURE_RANGE,
  activityAtPH,
  activityAtTemperature,
  type Enzyme,
} from './polymersBiomoleculesFood';

export type EnzymeInputKey = 'temperatureC' | 'pH';

export const ENZYME_EXPLORE_FIELDS: readonly ModelField<EnzymeInputKey>[] = [
  { key: 'temperatureC', label: 'Temperature', unit: '°C', min: TEMPERATURE_RANGE.min, max: TEMPERATURE_RANGE.max, step: 1 },
  { key: 'pH', label: 'pH', unit: 'pH', min: PH_RANGE.min, max: PH_RANGE.max, step: 0.1 },
];

const Q10_STEP_C = 10;

export interface EnzymeExploreResult {
  temperatureActivity: number;
  phActivity: number;
  steps: WorkedStep[];
  limitNote: string | null;
}

function pct(value: number): string {
  return `${value.toLocaleString('en-AU', { maximumFractionDigits: 1 })} %`;
}

function num(value: number, digits = 2): string {
  return value.toLocaleString('en-AU', { maximumFractionDigits: digits });
}

/** Signed number with a true minus sign, for substituted exponents. */
function signed(value: number, digits = 2): string {
  return value < 0 ? `−${num(Math.abs(value), digits)}` : num(value, digits);
}

/** Worked activity at the learner's temperature and pH. Throws the field message for invalid input. */
export function calculateEnzymeExplore(enzyme: Enzyme, input: Record<EnzymeInputKey, number>): EnzymeExploreResult {
  const error = firstModelError(ENZYME_EXPLORE_FIELDS, input);
  if (error) throw new Error(error);
  const temperatureActivity = activityAtTemperature(enzyme, input.temperatureC);
  const phActivity = activityAtPH(enzyme, input.pH);
  if (temperatureActivity === null || phActivity === null) throw new Error('The value is outside the model range.');

  const t = input.temperatureC;
  const topt = enzyme.optimumTemperatureC;
  const belowOptimum = t <= topt;
  const exponent = (t - topt) / Q10_STEP_C;
  const denaturedAt = topt + DENATURATION_SPAN_C;
  const distance = input.pH - enzyme.optimumPH;
  const denominator = 2 * PH_BELL_WIDTH * PH_BELL_WIDTH;

  const steps: WorkedStep[] = [
    {
      id: 'q10',
      title: 'Warming towards the optimum (Q₁₀ ≈ 2)',
      formula: `activity = ${PEAK_ACTIVITY} × ${ACTIVITY_Q10}^((T − T_opt) ÷ ${Q10_STEP_C})`,
      working: belowOptimum
        ? `${PEAK_ACTIVITY} × ${ACTIVITY_Q10}^((${num(t, 1)} − ${topt}) ÷ ${Q10_STEP_C}) = ${PEAK_ACTIVITY} × ${ACTIVITY_Q10}^(${signed(exponent)}) = ${pct(temperatureActivity)}`
        : `${num(t, 1)} °C is above T_opt = ${topt} °C, so the activity peaked at ${PEAK_ACTIVITY} % on the way up`,
      status: belowOptimum ? 'current' : 'done',
      note: belowOptimum ? undefined : `${PEAK_ACTIVITY} % at ${topt} °C`,
    },
    {
      id: 'denaturation',
      title: 'Denaturation above the optimum',
      formula: `activity = ${PEAK_ACTIVITY} × (1 − (T − T_opt) ÷ ${DENATURATION_SPAN_C})`,
      working: belowOptimum
        ? ''
        : t >= denaturedAt
          ? `${num(t, 1)} °C ≥ ${topt} + ${DENATURATION_SPAN_C} = ${denaturedAt} °C → 0 % (fully denatured)`
          : `${PEAK_ACTIVITY} × (1 − (${num(t, 1)} − ${topt}) ÷ ${DENATURATION_SPAN_C}) = ${pct(temperatureActivity)}`,
      status: belowOptimum ? 'not-reached' : 'current',
    },
    {
      id: 'ph',
      title: 'pH away from the optimum',
      formula: `activity = ${PEAK_ACTIVITY} × e^(−(pH − pH_opt)² ÷ (2 × ${PH_BELL_WIDTH}²))`,
      working: `${PEAK_ACTIVITY} × e^(−(${num(input.pH, 1)} − ${num(enzyme.optimumPH, 1)})² ÷ ${num(denominator)}) = ${PEAK_ACTIVITY} × e^(${signed(-(distance * distance) / denominator)}) = ${pct(phActivity)}`,
      status: 'current',
    },
  ];

  const limitNote =
    !belowOptimum && t >= denaturedAt
      ? `At ${denaturedAt} °C and above, the model enzyme is fully denatured. Cooling it again does not restore activity, and the model makes no prediction beyond 0 %.`
      : null;

  return { temperatureActivity, phActivity, steps, limitNote };
}

/** Input to the teaching tasks: the chosen enzyme with the result it produced. */
export interface EnzymeChallengeInput {
  enzyme: Enzyme;
  result: EnzymeExploreResult;
}

export const ENZYME_QUESTION = 'Why does an enzyme work fastest at one temperature and one pH, and slow down on either side?';

/** "Near the peak" band for the optimum tasks, in % of peak activity. */
const NEAR_PEAK_ACTIVITY = 90;
const PEPSIN_ID = 'pepsin';

/** Teaching tasks (CT-INTERACT C7). Each check reads only the enzyme and the model result. */
export const ENZYME_CHALLENGES: ExploreChallenge<EnzymeChallengeInput>[] = [
  {
    id: 'temperature-optimum',
    prompt: 'Find the temperature where the enzyme works fastest (temperature curve at 90 % or more).',
    hint: 'Warm it up from 25 °C and watch the temperature curve marker.',
    isMet: ({ result }) => result.temperatureActivity >= NEAR_PEAK_ACTIVITY,
    why: 'The optimum is 37 °C, body temperature. Below it, every 10 °C cooler halves the rate (Q₁₀ ≈ 2), so at 27 °C the model gives 50 %.',
  },
  {
    id: 'denatured',
    prompt: 'Show that hotter is not always faster: heat the enzyme until its activity drops to 0 %.',
    hint: 'Go well above the optimum.',
    isMet: ({ result }) => result.temperatureActivity === 0,
    why: 'From 37 + 15 = 52 °C the model enzyme is fully denatured: its active site has lost its shape. Cooling it again does not restore the activity, so this model stops at 0 %.',
  },
  {
    id: 'pepsin-acid',
    prompt: 'Choose pepsin and find a pH where it works at 90 % or more.',
    hint: 'Pepsin works in the stomach.',
    isMet: ({ enzyme, result }) => enzyme.id === PEPSIN_ID && result.phActivity >= NEAR_PEAK_ACTIVITY,
    why: 'Not every enzyme works best at pH 7. Pepsin peaks near pH 2 in stomach acid; at pH 2 salivary amylase (optimum pH 6.8) is at about 0.03 %.',
  },
];
